import { useQuery } from '@tanstack/react-query';
import { Plug, CheckCircle2, AlertCircle, Link2, AlertTriangle } from 'lucide-react';
import WidgetShell from '@/components/admin/dashboard/WidgetShell';
import StatCard from '@/components/admin/dashboard/StatCard';
import api from '@/lib/api';

/** Mirrors `/admin/ownerrez/status` (ownerrez.ts → getConnection + counts). */
interface Connection {
  auth_kind: 'oauth' | 'pat';
  account_email: string | null;
  status: 'connected' | 'error' | 'disconnected';
  last_synced_at: string | null;
  last_error: string | null;
  ownerrez_webhook_id: string | null;
}

interface StatusResponse {
  connection: Connection | null;
  counts: { mapped: number; conflicts: number };
  oauth_available: boolean;
}

export default function OwnerRezStatusWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'ownerrez', 'status'],
    queryFn: () => api.get<StatusResponse>('/admin/ownerrez/status'),
    staleTime: 60000,
  });

  const connection = data?.connection ?? null;

  return (
    <WidgetShell
      title="OwnerRez sync"
      icon={<Plug />}
      to="/admin/ownerrez"
      linkLabel="Manage"
      isLoading={isLoading}
      error={error}
      isEmpty={!!data && !connection}
      emptyIcon={<Plug />}
      emptyMessage="OwnerRez not connected"
    >
      {data && connection && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm">
            {connection.status === 'connected' ? (
              <span className="inline-flex items-center gap-1.5 font-medium text-green-700">
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Connected
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 font-medium text-red-700">
                <AlertCircle className="h-4 w-4" aria-hidden="true" /> Connection error
              </span>
            )}
            {connection.account_email && (
              <span className="truncate text-xs text-stone-500">{connection.account_email}</span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <StatCard
              title="Mapped"
              value={data.counts.mapped}
              icon={<Link2 />}
              tone="blue"
              subtitle="properties"
            />
            <StatCard
              title="Conflicts"
              value={data.counts.conflicts}
              icon={<AlertTriangle />}
              tone={data.counts.conflicts > 0 ? 'red' : 'stone'}
              subtitle="to resolve"
            />
          </div>
        </div>
      )}
    </WidgetShell>
  );
}
